import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

if (!supabaseUrl) {
  throw new Error('REACT_APP_SUPABASE_URL is not configured in your environment variables.');
}

if (!supabaseAnonKey) {
  throw new Error('REACT_APP_SUPABASE_ANON_KEY is not configured in your environment variables.');
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true
  }
});

export interface Project {
  id: string;
  name: string;
  description: string | null;
  created_at: string;
  updated_at: string;
}

export const getProjects = async (): Promise<Project[]> => {
  const {
    data: { user },
    error: userError,
  } = await supabase.auth.getUser();

  if (userError) {
    throw userError;
  }

  if (!user) {
    throw new Error('User not authenticated');
  }

  const { data: projects, error } = await supabase
    .from('projects')
    .select('*')
    .eq('user_id', user.id)
    .order('created_at', { ascending: false });

  if (error) {
    throw error;
  }

  if (!projects) {
    return [];
  }

  return projects;
};

export const createProject = async (name: string = 'Untitled Project', description: string | null = null): Promise<Project> => {
  const {
    data: { user },
    error: userError,
  } = await supabase.auth.getUser();

  if (userError) {
    console.error('Auth error:', userError);
    throw userError;
  }

  if (!user) {
    console.error('No user found');
    throw new Error('User not authenticated');
  }

  try {
    const { data, error } = await supabase
      .from('projects')
      .insert([{ 
        name, 
        description,
        user_id: user.id 
      }])
      .select()
      .single();

    if (error) {
      console.error('Project creation error:', error);
      throw error;
    }

    if (!data) {
      throw new Error('No data returned after project creation');
    }

    return data;
  } catch (error) {
    console.error('Project creation failed:', error);
    throw error;
  }
};
